import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, animate } from 'framer-motion';
/* ------------ CountUp helper ------------ */
function CountUp({
  to,
  prefix = '',
  suffix = '',
  duration = 1.2,
  delay = 0,
  decimals = 0,
  trigger
}: {
  to: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  delay?: number;
  decimals?: number;
  trigger: boolean;
}) {
  const [value, setValue] = useState(0);
  const fired = useRef(false);
  useEffect(() => {
    if (!trigger || fired.current) return;
    fired.current = true;
    const controls = animate(0, to, {
      duration,
      delay,
      ease: 'easeOut',
      onUpdate: v => setValue(v)
    });
    return () => controls.stop();
  }, [trigger, to, duration, delay]);
  const formatted = decimals ? value.toFixed(decimals).replace('.', ',') : Math.round(value).toLocaleString('pt-BR');
  return <span data-id="element-496">
      {prefix}
      {formatted}
      {suffix}
    </span>;
}
/* ------------ Demo 1 — Cálculo automático ------------ */
function Demo1({
  inView
}: {
  inView: boolean;
}) {
  return <div className="bg-white rounded-2xl border border-line p-4 md:p-5 shadow-sm" data-id="element-497">
      <div className="space-y-2.5" data-id="element-498">
        <div data-id="element-499">
          <Row label="Custo" value={<CountUp to={32} prefix="R$ " duration={1} delay={0.1} trigger={inView} data-id="element-501" />} data-id="element-500" />
          <div className="text-[11px] md:text-[12px] text-ink-soft mt-0.5 leading-snug" data-id="element-502">
            incluso insumo, embalagem, mão de obra — tudo
          </div>
        </div>
        <Row label="Margem" value={<CountUp to={60} suffix="%" duration={1} delay={0.4} trigger={inView} data-id="element-504" />} data-id="element-503" />
        <div className="border-t border-line my-2" data-id="element-505" />
        <Row highlight label="Preço" value={<CountUp to={80} prefix="R$ " duration={1.2} delay={0.8} trigger={inView} data-id="element-507" />} data-id="element-506" />
      </div>
    </div>;
}
function Row({
  label,
  value,
  highlight
}: {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}) {
  return <div className="flex items-center justify-between" data-id="element-508">
      <span className={`text-[14px] md:text-[15px] ${highlight ? 'font-semibold text-ink' : 'text-ink-soft'}`} data-id="element-509">
        {label}
      </span>
      <span className={`font-bold tabular-nums ${highlight ? 'text-rose text-xl md:text-2xl' : 'text-ink text-base md:text-lg'}`} data-id="element-510">
        {value}
      </span>
    </div>;
}
/* ------------ Demo 2 — Recálculo em cascata ------------ */
function Demo2({
  inView
}: {
  inView: boolean;
}) {
  return <div className="bg-white rounded-2xl border border-line p-5 md:p-6 shadow-sm space-y-4" data-id="element-511">
      {/* Material */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      delay: 0.1
    }} className="bg-gold-soft/50 rounded-xl p-3.5 border border-gold/20" data-id="element-512">
        <div className="text-[11px] md:text-xs font-semibold tracking-wide text-ink-soft uppercase mb-1" data-id="element-513">
          Material
        </div>
        <div className="flex items-center justify-between gap-2" data-id="element-514">
          <span className="font-semibold text-ink text-[14px] md:text-[15px]" data-id="element-515">
            Leite condensado
          </span>
          <span className="flex items-center gap-1.5 tabular-nums text-[14px] md:text-[15px]" data-id="element-516">
            <span className="text-ink-soft line-through" data-id="element-517">R$ 8</span>
            <ArrowFlow data-id="element-518" />
            <span className="font-bold text-rose" data-id="element-519">R$ 11</span>
            <span aria-hidden="true" data-id="element-520">⚠️</span>
          </span>
        </div>
      </motion.div>

      {/* Cascading arrow */}
      <motion.div initial={{
      opacity: 0,
      scaleY: 0
    }} animate={inView ? {
      opacity: 1,
      scaleY: 1
    } : {}} transition={{
      duration: 0.4,
      delay: 0.9,
      ease: 'easeOut'
    }} className="flex justify-center origin-top" style={{
      transformOrigin: 'top'
    }} data-id="element-521">
        <svg width="20" height="22" viewBox="0 0 20 22" fill="none" aria-hidden="true" data-id="element-522">
          <path d="M10 0v18M3 13l7 7 7-7" stroke="#E32F6E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-523" />
        </svg>
      </motion.div>

      {/* Produto recalculado */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      delay: 1.2
    }} className="bg-rose-soft rounded-xl p-3.5 border border-rose/20" data-id="element-524">
        <div className="text-[11px] md:text-xs font-semibold tracking-wide text-rose-deep uppercase mb-1" data-id="element-525">
          Produto
        </div>
        <div className="flex items-center justify-between gap-2" data-id="element-526">
          <span className="font-semibold text-ink text-[14px] md:text-[15px]" data-id="element-527">
            Brigadeiro
          </span>
          <span className="flex items-center gap-1.5 tabular-nums text-[14px] md:text-[15px]" data-id="element-528">
            <span className="text-ink-soft line-through" data-id="element-529">R$ 2,40</span>
            <ArrowFlow data-id="element-530" />
            <span className="font-bold text-rose" data-id="element-531">R$ 2,90</span>
            <span aria-hidden="true" data-id="element-532">✓</span>
          </span>
        </div>
      </motion.div>
    </div>;
}
function ArrowFlow() {
  return <svg width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true" className="text-rose" data-id="element-533">
      <path d="M1 5h11M8 1l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-534" />
    </svg>;
}
/* ------------ Demo 3 — Resumo mensal ------------ */
function Demo3({
  inView
}: {
  inView: boolean;
}) {
  return <div className="bg-white rounded-2xl border border-line p-5 md:p-6 shadow-sm" data-id="element-535">
      <div className="flex items-center justify-between mb-4" data-id="element-536">
        <span className="text-[12px] md:text-sm font-semibold text-ink-soft tracking-wide" data-id="element-537">
          Abril 2026
        </span>
        <span className="text-[11px] md:text-xs px-2 py-0.5 rounded-full bg-green-soft text-ink font-semibold" data-id="element-538">
          Fechado
        </span>
      </div>
      <div className="space-y-3" data-id="element-539">
        <SummaryRow label="Faturei" icon="💰" value={<CountUp to={7540} prefix="R$ " duration={1.4} delay={0.1} trigger={inView} data-id="element-541" />} data-id="element-540" />
        <SummaryRow label="Lucrei" icon="✓" iconClass="text-rose" value={<CountUp to={4373} prefix="R$ " duration={1.4} delay={0.4} trigger={inView} data-id="element-543" />} valueClass="text-rose" data-id="element-542" />
        <div className="border-t border-line pt-3 flex items-center justify-between" data-id="element-544">
          <span className="text-[14px] md:text-[15px] font-semibold text-ink" data-id="element-545">
            Margem
          </span>
          <span className="flex items-baseline gap-2 tabular-nums" data-id="element-546">
            <span className="text-xl md:text-2xl font-bold text-ink" data-id="element-547">
              <CountUp to={58} suffix="%" duration={1.2} delay={0.7} trigger={inView} data-id="element-548" />
            </span>
            <motion.span initial={{
            opacity: 0,
            y: 4
          }} animate={inView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.4,
            delay: 1.6
          }} className="text-xs md:text-sm font-bold text-rose flex items-center gap-0.5" data-id="element-549">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" data-id="element-550">
                <path d="M5 1v8M2 4l3-3 3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-551" />
              </svg>
              +4%
            </motion.span>
          </span>
        </div>
      </div>
    </div>;
}
function SummaryRow({
  label,
  value,
  icon,
  iconClass,
  valueClass
}: {
  label: string;
  value: React.ReactNode;
  icon: string;
  iconClass?: string;
  valueClass?: string;
}) {
  return <div className="flex items-center justify-between" data-id="element-552">
      <span className="flex items-center gap-2 text-[14px] md:text-[15px] text-ink-soft" data-id="element-553">
        <span className={iconClass} data-id="element-554">{icon}</span>
        {label}:
      </span>
      <span className={`font-bold tabular-nums text-base md:text-lg ${valueClass || 'text-ink'}`} data-id="element-555">
        {value}
      </span>
    </div>;
}
/* ------------ Demo wrapper card ------------ */
interface DemoBlockProps {
  number: string;
  title: React.ReactNode;
  body: string;
  demo: React.ReactNode;
  delay: number;
  inView: boolean;
}
function DemoBlock({
  number,
  title,
  body,
  demo,
  delay,
  inView
}: DemoBlockProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: 'easeOut'
  }} className="bg-bg-card rounded-2xl p-4 md:p-6 border border-line shadow-soft" data-id="element-556">
      <div className="flex items-baseline gap-2.5 mb-3" data-id="element-557">
        <span className="font-serif font-bold text-3xl md:text-4xl text-rose leading-none" data-id="element-558">
          {number}
        </span>
        <h3 className="font-serif font-bold text-lg md:text-xl text-ink leading-tight tracking-[-0.01em]" data-id="element-559">
          {title}
        </h3>
      </div>

      <div className="mb-3" data-id="element-560">{demo}</div>

      <p className="text-[13px] md:text-[14px] text-ink-soft leading-relaxed" data-id="element-561">
        {body}
      </p>
    </motion.div>;
}
/* ------------ Section ------------ */
export function RoutineTransformationSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, {
    once: true,
    margin: '-80px'
  });
  return <section id="transformacao" ref={sectionRef} className="py-12 md:py-16 px-5 bg-white relative overflow-hidden" data-id="element-562">
      {/* Decorative tint */}
      <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-rose-soft/30 to-transparent pointer-events-none" data-id="element-563" />

      <div className="max-w-3xl mx-auto relative z-10" data-id="element-564">
        {/* Eyebrow */}
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center text-rose font-caveat text-4xl md:text-5xl mb-3" data-id="element-565">
          a transformação
        </motion.p>

        {/* Headline */}
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: 'easeOut'
      }} className="text-center text-3xl md:text-5xl font-serif font-bold mb-3 leading-tight text-ink tracking-[-0.02em]" data-id="element-566">
          Veja o que muda
          <br data-id="element-567" />
          na sua rotina.
        </motion.h2>

        {/* Subheadline */}
        <motion.p initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.6,
        delay: 0.25
      }} className="text-center text-ink-soft text-[14px] md:text-[15px] mb-8 md:mb-10 max-w-lg mx-auto" data-id="element-568">
          Três coisas que a Aurora faz por você, sem você precisar pensar.
        </motion.p>

        {/* Mini-demos stack */}
        <div className="space-y-4 md:space-y-5 mb-8" data-id="element-569">
          <DemoBlock number="1." title={<>Calcula o preço justo na hora.</>} body="Você diz quanto pagou pelos materiais. A Aurora calcula tudo o resto." demo={<Demo1 inView={isInView} data-id="element-571" />} delay={0.35} inView={isInView} data-id="element-570" />
          <DemoBlock number="2." title={<>Quando um material sobe, seus preços sobem juntos.</>} body="Você atualiza UM material. Todos os produtos que usam ele se ajustam sozinhos." demo={<Demo2 inView={isInView} data-id="element-573" />} delay={0.5} inView={isInView} data-id="element-572" />
          <DemoBlock number="3." title={<>No fim do mês, você sabe.</>} body="Sem planilha, sem somar nota fiscal, sem chutar. Você abre o app e sabe." demo={<Demo3 inView={isInView} data-id="element-575" />} delay={0.65} inView={isInView} data-id="element-574" />
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.85,
        ease: 'easeOut'
      }} className="flex flex-col items-center gap-3" data-id="element-576">
          <a href="#preco" onClick={e => {
          e.preventDefault();
          document.getElementById('preco')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="inline-flex items-center gap-3 bg-rose text-white pl-8 pr-2 py-3 rounded-full hover:bg-rose-deep transition-all hover:scale-105 shadow-md w-full sm:w-auto justify-center no-underline group" data-id="element-577">
            <span className="font-medium" data-id="element-578">Quero começar agora</span>
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center group-hover:translate-x-0.5 transition-transform" data-id="element-579">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-580">
                <path d="M5 12h14M13 5l7 7-7 7" data-id="element-581" />
              </svg>
            </div>
          </a>
          <p className="text-[13px] text-ink-soft" data-id="element-582">
            R$ 29,90/mês · 7 dias de garantia
          </p>
        </motion.div>
      </div>
    </section>;
}